// ====== LANGUAGE SUPPORT ======

export type LanguageCode = 'en' | 'es' | 'fr' | 'ko' | 'pl' | 'pt' | 'ru' | 'tl' | 'vi' | 'zh';

export interface LanguageOption {
  code: LanguageCode;
  name: string;
  native: string;
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'es', name: 'Spanish', native: 'Español' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'ko', name: 'Korean', native: '한국어' },
  { code: 'pl', name: 'Polish', native: 'Polski' },
  { code: 'pt', name: 'Portuguese', native: 'Português' },
  { code: 'ru', name: 'Russian', native: 'Русский' },
  { code: 'tl', name: 'Tagalog', native: 'Tagalog' },
  { code: 'vi', name: 'Vietnamese', native: 'Tiếng Việt' },
  { code: 'zh', name: 'Chinese', native: '中文' },
];

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

// Language options for select components
export const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map(lang => ({
  value: lang.code,
  label: `${lang.native} (${lang.name})`,
}));

/**
 * Create empty multilingual text object for given languages
 * @param languages - Array of language codes
 * @returns MultilingualText object with empty strings for each language
 */
export const createEmptyMultilingualText = (languages: string[]): { [key: string]: string } => {
  return languages.reduce((acc, lang) => {
    acc[lang] = '';
    return acc;
  }, {} as { [key: string]: string });
};

/**
 * Get language name by code
 * @param code - Language code
 * @returns Language name or code if not found
 */
export const getLanguageName = (code: string): string => {
  const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
  return lang ? lang.native : code.toUpperCase();
};

// ====== US STATE ABBREVIATIONS ======

export const US_STATES: string[] = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

// ====== VEHICLE TYPES ======

export const VEHICLE_TYPES = ['CAR', 'MOTORCYCLE', 'CDL'] as const;

export type VehicleType = typeof VEHICLE_TYPES[number];

// Vehicle type mapping (for parsing - maps invalid types to valid ones)
export const VEHICLE_TYPE_MAPPING: Record<string, VehicleType> = {
  'BUS': 'CDL',
  'TRUCK': 'CDL',
};

// Vehicle type options for select components
export const VEHICLE_TYPE_OPTIONS = VEHICLE_TYPES.map(type => ({
  value: type,
  label: type,
}));

// ====== TOPIC OPTIONS ======

export const TOPICS = [
  'braking',
  'drinking-driving',
  'driver-license',
  'driving',
  'emergency-on-road',
  'fines-and-limits',
  'freeways',
  'maneuvers-signaling',
  'parking-regulations',
  'passing',
  'priority-on-road',
  'railroad-crossings',
  'road-markings',
  'road-signs',
  'road-work',
  'roundabouts',
  'sharing-pedestrians',
  'sharing-road',
  'sharing-school-bus',
  'traffic-regulations',
  'use-of-lights',
  'weather-conditions',
] as const;

export type Topic = typeof TOPICS[number];

/** Closed set of question / image topic slugs (same as {@link TOPICS}). For membership checks and AI prompts. */
export const MIGRATION_TOPIC_SET: ReadonlySet<Topic> = new Set(TOPICS);

/** Single-line closed topic list for OpenAI prompts (Milestone 3). */
export const MIGRATION_TOPICS_AI_ALLOWLIST_TEXT = `Closed question-bank topic slugs (kebab-case; metadata and images must use one of these): ${TOPICS.join(
  ', '
)}.`;

/**
 * Normalize free-form topic input to a slug comparable to {@link TOPICS} (underscores/spaces → hyphens).
 */
export function normalizeQuestionTopicSlugForValidation(raw: string): string {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** True iff `raw` normalizes to an entry in {@link TOPICS}. */
export function isAllowedQuestionTopicSlug(raw: string): boolean {
  const slug = normalizeQuestionTopicSlugForValidation(raw);
  return MIGRATION_TOPIC_SET.has(slug as Topic);
}

/**
 * Retired question-topic slugs still present on persisted documents → canonical fallback.
 * Keep in sync with prod→prod2 / Test Catalog remaps.
 */
export const RETIRED_QUESTION_TOPIC_FALLBACKS: Readonly<Record<string, Topic>> = {
  'distracted-driving': 'driving',
};

/** True when `raw` is a known retired slug (before fallback). */
export function isRetiredQuestionTopicSlug(raw: string): boolean {
  const slug = normalizeQuestionTopicSlugForValidation(raw);
  return Boolean(slug && RETIRED_QUESTION_TOPIC_FALLBACKS[slug]);
}

/**
 * Resolve a stored or AI topic string to a closed {@link TOPICS} slug.
 * Allowed → itself; retired (e.g. distracted-driving) → mapped fallback; else → residual (default driving).
 */
export function coerceQuestionTopicSlug(raw: string, residual: Topic = 'driving'): Topic {
  const slug = normalizeQuestionTopicSlugForValidation(raw);
  if (!slug) return residual;
  if (MIGRATION_TOPIC_SET.has(slug as Topic)) return slug as Topic;
  const retired = RETIRED_QUESTION_TOPIC_FALLBACKS[slug];
  if (retired) return retired;
  return residual;
}

// Topic options for select components
export const TOPIC_OPTIONS = TOPICS.map(topic => ({
  value: topic,
  label: topic.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' '),
}));

// ====== DIFFICULTY OPTIONS ======

export const DIFFICULTIES = ['soft', 'medium', 'hard'] as const;

export type Difficulty = typeof DIFFICULTIES[number];

// Difficulty options for select components
export const DIFFICULTY_OPTIONS = DIFFICULTIES.map(difficulty => ({
  value: difficulty,
  label: difficulty.charAt(0).toUpperCase() + difficulty.slice(1),
}));

// ====== IMAGE REQUIRED OPTIONS ======

export const IMAGE_REQUIRED_OPTIONS = ['true', 'false', 'optional'] as const;

export type ImageRequired = typeof IMAGE_REQUIRED_OPTIONS[number];

// Image required options for select components
export const IMAGE_REQUIRED_OPTIONS_LIST = IMAGE_REQUIRED_OPTIONS.map(option => ({
  value: option,
  label: option.charAt(0).toUpperCase() + option.slice(1),
}));

// ====== STATUS OPTIONS ======

export const STATUS_OPTIONS = ['draft', 'review', 'approved'] as const;

export type Status = typeof STATUS_OPTIONS[number];

// Status options for select components
export const STATUS_OPTIONS_LIST = STATUS_OPTIONS.map(status => ({
  value: status,
  label: status.charAt(0).toUpperCase() + status.slice(1),
}));

// ====== FIREBASE COLLECTION HELPERS ======

/**
 * Get Firebase collection name for questions by state
 * @param state - Two-letter US state code (e.g., 'NJ', 'NY')
 * @returns Collection name (e.g., `questions_NJ`) — **only for the app Default Firestore**. VehiWheel QA/prod banks use
 * lowercase ids (`questions_nj`, …); do not use this helper for those databases.
 */
export const getQuestionsCollectionName = (state: string): string => {
  return `questions_${state.toUpperCase()}`;
};

// ====== QUESTION ID VALIDATION ======

/**
 * Validate question markdownId format
 * Base: {STATE}-{TOPIC}-{NUMBER} (e.g. "NJ-DRIVING-001", "NJ-DRIVING-1012")
 * Duplicate copies / migration hash: zero or more trailing segments `-` + 4–6 alphanumeric chars
 * (e.g. "NJ-DRIVING-017-A3F9", "NJ-DRIVING-001-ABCDE", "NJ-DRIVING-017-A3F9-1B2C3D").
 * @param markdownId - Question markdownId to validate
 * @returns true if valid format
 */
export const isValidMarkdownIdFormat = (markdownId: string): boolean => {
  const trimmed = (markdownId || '').trim().toUpperCase();
  if (!trimmed) return false;
  const pattern = /^[A-Z]{2}-[A-Z-]+-\d+(?:-(?:[A-Z0-9]{4}|[A-Z0-9]{5}|[A-Z0-9]{6}))*$/;
  return pattern.test(trimmed);
};

/**
 * When the user pastes a common-bank style id (e.g. `DRIVING-282` with no state prefix), prefix the
 * selected two-letter state so it matches {@link isValidMarkdownIdFormat} (e.g. `NJ-DRIVING-282`).
 * Idempotent: already valid state ids and ids that already start with `STATE-` are left unchanged.
 */
export const toStatePrefixedMarkdownId = (markdownId: string, stateCode: string): string => {
  const t = (markdownId || '').trim().toUpperCase();
  const st = (stateCode || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 2);
  if (!t || st.length !== 2) return t;
  if (isValidMarkdownIdFormat(t)) return t;
  if (t.startsWith(`${st}-`)) return t;
  // First segment looks like a state code; do not guess (e.g. `DR-…` or another `XX-…` prefix).
  if (/^[A-Z]{2}-/.test(t)) return t;
  if (/^[A-Z0-9][A-Z0-9-]*-\d+$/.test(t)) {
    return `${st}-${t}`;
  }
  return t;
};

// ====== DRAFT STATUS CALCULATION ======

/**
 * Calculate draft status based on image requirement and whether both image URLs are set
 * @param imageRequired - Image requirement ('true', 'false', 'optional')
 * @param hasImage - True when both full and medium image URLs are present (same meaning as stored `hasImage`)
 * @returns true if question should be marked as draft
 */
export const calculateDraftStatus = (
  imageRequired: ImageRequired,
  hasImage: boolean
): boolean => {
  if (imageRequired === 'true' && !hasImage) {
    return true;
  }
  return false;
};

// ====== VEHICLE TYPE NORMALIZATION ======

/**
 * Normalize vehicle type (maps invalid types to valid ones)
 * @param vehicle - Vehicle type string
 * @returns Normalized vehicle type ('CAR', 'MOTORCYCLE', or 'CDL')
 */
export const normalizeVehicleType = (vehicle: string): VehicleType => {
  const upper = vehicle.toUpperCase().trim();
  
  // Check if it's already a valid type
  if (VEHICLE_TYPES.includes(upper as VehicleType)) {
    return upper as VehicleType;
  }
  
  // Map invalid types to valid ones
  if (upper in VEHICLE_TYPE_MAPPING) {
    return VEHICLE_TYPE_MAPPING[upper];
  }
  
  // Default to CDL for unknown types
  return 'CDL';
};
